import { useState, useEffect } from 'react'
import { Card, Row, Col, Form, Button, Spinner, Alert, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { useSyncedLocalClientId } from '../../hooks/useSyncedLocalClientId'
import { formatCurrency } from '../../utils/formatters'
import { getMonthStartDateOnlyMx, getTodayDateOnlyMx, startOfLocalDayFromApi } from '../../utils/apiDateFormat'

const BalanceGeneralReport = ({ onDrillDown }) => {
  const { clientId: clienteId, setClientId: setClienteId, selectDisabled } = useSyncedLocalClientId()
  const [fechaCorte, setFechaCorte] = useState(getTodayDateOnlyMx())
  const [exporting, setExporting] = useState(false)

  const handleAccountClick = (accountId, _accountCode) => {
    if (onDrillDown && accountId) {
      // Calcular fecha de inicio del mes actual para el auxiliar
      const startDate = getMonthStartDateOnlyMx()
      onDrillDown({
        clientId: clienteId,
        accountId: accountId.toString(),
        startDate: startDate,
        endDate: fechaCorte,
        fromReport: 'Balance General',
        fromReportTab: 'balance-general'
      }, 'auxiliar-cuenta')
    }
  }

  const handleExport = async (format) => {
    try {
      setExporting(true)
      const params = new URLSearchParams()
      if (clienteId) params.append('client_id', clienteId)
      if (fechaCorte) params.append('fecha_corte', fechaCorte)
      params.append('format', format)
      
      const response = await api.get(`/financial/reportes/exportar/balance-general/?${params.toString()}`, {
        responseType: 'blob'
      })
      
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `Balance_General.${format === 'excel' ? 'xlsx' : 'pdf'}`)
      document.body.appendChild(link)
      link.click()
      link.remove()
    } catch (error) {
      console.error('Error exportando:', error)
    } finally {
      setExporting(false)
    }
  }

  // Obtener lista de clientes para el filtro
  const { data: clientesData, isLoading: isLoadingClients } = useQuery(
    'clients',
    async () => {
      const response = await api.get('/clients/clients/?activo=true')
      return response.data.results || response.data || []
    }
  )
  const clients = Array.isArray(clientesData) ? clientesData : []

  // Obtener datos del Balance General
  const { data: reportData, isLoading: isLoadingReport, error: reportError, refetch } = useQuery(
    ['balance-general-report', clienteId, fechaCorte],
    async () => {
      const params = new URLSearchParams()
      if (clienteId) params.append('client_id', clienteId)
      if (fechaCorte) params.append('date_cutoff', fechaCorte)
      const response = await api.get(`/financial/reportes/balance-general/?${params.toString()}`)
      return response.data
    },
    { 
      enabled: !!clienteId && !!fechaCorte, // Solo habilitar la consulta si el cliente y fecha están presentes
      onError: (err) => {
        console.error("Error al cargar Balance General", err)
      }
    }
  )

  const handleSubmit = (e) => {
    e.preventDefault()
    
    if (!clienteId) {
      toast.error('Por favor, seleccione un cliente.')
      return
    }
    
    if (!fechaCorte) {
      toast.error('Por favor, seleccione una fecha de corte.')
      return
    }
    
    // Validar que la fecha de corte no sea futura
    const fechaCorteDate = startOfLocalDayFromApi(fechaCorte)
    const hoy = startOfLocalDayFromApi(getTodayDateOnlyMx())
    if (fechaCorteDate && hoy && fechaCorteDate > hoy) {
      toast.error('La fecha de corte no puede ser futura.')
      return
    }
    
    refetch()
  }

  // Shortcuts de teclado
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ctrl/Cmd + Enter para generar reporte
      if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault()
        const form = e.target.closest('form')
        if (form) {
          const submitButton = form.querySelector('button[type="submit"]')
          if (submitButton && !submitButton.disabled) {
            submitButton.click()
          }
        }
      }
      // Ctrl/Cmd + S para exportar a Excel
      if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault()
        if (reportData && !exporting) {
          handleExport('excel')
        }
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [reportData, exporting]) // eslint-disable-line react-hooks/exhaustive-deps

  if (isLoadingClients) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" variant="primary" />
        <p className="mt-2">Cargando clientes...</p>
      </div>
    )
  }

  return (
    <div>
      <Card className="mb-4">
        <Card.Header as="h5">Estado de Situación Financiera (Balance General)</Card.Header>
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group controlId="formClient">
                  <Form.Label>Cliente</Form.Label>
                  <Form.Select
                    value={clienteId}
                    onChange={(e) => setClienteId(e.target.value)}
                    disabled={selectDisabled}
                    required
                  >
                    <option value="">Seleccione un cliente</option>
                    {clients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.razon_social || client.business_name || `${client.nombre} ${client.apellido_paterno || ''}`}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group controlId="formFechaCorte">
                  <Form.Label>
                    Fecha de Corte
                    <OverlayTrigger
                      placement="top"
                      overlay={
                        <Tooltip>
                          Seleccione la fecha de corte para el Balance General. No puede ser una fecha futura.
                        </Tooltip>
                      }
                    >
                      <i className="bi bi-info-circle ms-2 text-info" style={{ cursor: 'help' }}></i>
                    </OverlayTrigger>
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={fechaCorte}
                    onChange={(e) => setFechaCorte(e.target.value)}
                    required
                    max={getTodayDateOnlyMx()}
                  />
                  {fechaCorte && startOfLocalDayFromApi(fechaCorte) > startOfLocalDayFromApi(getTodayDateOnlyMx()) && (
                    <Form.Text className="text-danger">
                      La fecha de corte no puede ser futura.
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row>
              <Col xs={12} className="d-flex gap-2 flex-wrap">
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Genera el Balance General con los filtros seleccionados (Ctrl+Enter)</Tooltip>}
                >
                  <Button variant="primary" type="submit" disabled={isLoadingReport || !clienteId || !fechaCorte} className="flex-grow-1 flex-md-grow-0">
                    {isLoadingReport ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        Generando...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-file-earmark-text me-2"></i>
                        Generar Reporte
                      </>
                    )}
                  </Button>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Exporta el reporte actual a formato PDF</Tooltip>}
                >
                  <span>
                    <Button variant="outline-danger" onClick={() => handleExport('pdf')} disabled={!reportData || exporting || isLoadingReport} className="flex-grow-1 flex-md-grow-0">
                      {exporting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Exportando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-file-earmark-pdf me-2"></i>
                          <span className="d-none d-md-inline">Exportar </span>PDF
                        </>
                      )}
                    </Button>
                  </span>
                </OverlayTrigger>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip>Exporta el reporte actual a formato Excel (Ctrl+S)</Tooltip>}
                >
                  <span>
                    <Button variant="outline-success" onClick={() => handleExport('excel')} disabled={!reportData || exporting || isLoadingReport} className="flex-grow-1 flex-md-grow-0">
                      {exporting ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Exportando...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-file-earmark-excel me-2"></i>
                          <span className="d-none d-md-inline">Exportar </span>Excel
                        </>
                      )}
                    </Button>
                  </span>
                </OverlayTrigger>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {reportError && (
        <Alert variant="danger" dismissible>
          <Alert.Heading>Error al cargar el reporte</Alert.Heading>
          <p className="mb-0">
            {reportError.response?.data?.detail || reportError.response?.data?.error || reportError.message || 'Ocurrió un error al generar el Balance General. Por favor, intente nuevamente.'}
          </p>
          {reportError.response?.status && (
            <small className="text-muted">Código de error: {reportError.response.status}</small>
          )}
        </Alert>
      )}

      {!reportData && !isLoadingReport && clienteId && fechaCorte && (
        <Alert variant="info">
          <i className="bi bi-info-circle me-2"></i>
          Haga clic en &apos;Generar Reporte&apos; para ver el Balance General.
        </Alert>
      )}

      {isLoadingReport && (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" size="lg" />
          <p className="mt-3 text-muted">Generando Balance General...</p>
        </div>
      )}

      {reportData && (
        <>
          <Row>
            {/* Activos */}
            <Col md={6}>
              <Card className="mb-4">
                <Card.Header as="h6" className="bg-light">Activos</Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>Cuenta</th>
                        <th className="text-end">Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.activos?.cuentas?.map((cuenta) => (
                        <tr key={cuenta.code}>
                          <td>
                            {cuenta.id && onDrillDown ? (
                              <a 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault()
                                  handleAccountClick(cuenta.id, cuenta.code)
                                }}
                                className="text-decoration-none"
                                title="Ver auxiliar de cuenta"
                              >
                                <span className="text-muted">{cuenta.code}</span> - {cuenta.name}
                                <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                              </a>
                            ) : (
                              <>
                                <span className="text-muted">{cuenta.code}</span> - {cuenta.name}
                              </>
                            )}
                          </td>
                          <td className="text-end">{formatCurrency(cuenta.balance)}</td>
                        </tr>
                      ))}
                      <tr className="table-info">
                        <td><strong>Total Activos</strong></td>
                        <td className="text-end"><strong>{formatCurrency(reportData.activos?.total || 0)}</strong></td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            </Col>

            {/* Pasivos y Capital */}
            <Col md={6}>
              {/* Pasivos */}
              <Card className="mb-4">
                <Card.Header as="h6" className="bg-light">Pasivos</Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>Cuenta</th>
                        <th className="text-end">Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.pasivos?.cuentas?.map((cuenta) => (
                        <tr key={cuenta.code}>
                          <td>
                            {cuenta.id && onDrillDown ? (
                              <a 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault()
                                  handleAccountClick(cuenta.id, cuenta.code)
                                }}
                                className="text-decoration-none"
                                title="Ver auxiliar de cuenta"
                              >
                                <span className="text-muted">{cuenta.code}</span> - {cuenta.name}
                                <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                              </a>
                            ) : (
                              <>
                                <span className="text-muted">{cuenta.code}</span> - {cuenta.name}
                              </>
                            )}
                          </td>
                          <td className="text-end">{formatCurrency(cuenta.balance)}</td>
                        </tr>
                      ))}
                      <tr className="table-info">
                        <td><strong>Total Pasivos</strong></td>
                        <td className="text-end"><strong>{formatCurrency(reportData.pasivos?.total || 0)}</strong></td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              {/* Capital */}
              <Card className="mb-4">
                <Card.Header as="h6" className="bg-light">Capital</Card.Header>
                <Card.Body>
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                      <tr>
                        <th>Cuenta</th>
                        <th className="text-end">Saldo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.capital?.cuentas?.map((cuenta) => (
                        <tr key={cuenta.code}>
                          <td>
                            {cuenta.id && onDrillDown ? (
                              <a 
                                href="#" 
                                onClick={(e) => {
                                  e.preventDefault()
                                  handleAccountClick(cuenta.id, cuenta.code)
                                }}
                                className="text-decoration-none"
                                title="Ver auxiliar de cuenta"
                              >
                                <span className="text-muted">{cuenta.code}</span> - {cuenta.name}
                                <i className="ms-2 bi bi-arrow-right-circle" style={{ fontSize: '0.9em' }}></i>
                              </a>
                            ) : (
                              <>
                                <span className="text-muted">{cuenta.code}</span> - {cuenta.name}
                              </>
                            )}
                          </td>
                          <td className="text-end">{formatCurrency(cuenta.balance)}</td>
                        </tr>
                      ))}
                      {reportData.capital?.resultado_ejercicio !== undefined && (
                        <tr className="table-warning">
                          <td><em>Resultado del Ejercicio (Calculado)</em></td>
                          <td className="text-end">{formatCurrency(reportData.capital.resultado_ejercicio)}</td>
                        </tr>
                      )}
                      <tr className="table-info">
                        <td><strong>Total Capital</strong></td>
                        <td className="text-end"><strong>{formatCurrency(reportData.capital?.total || 0)}</strong></td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              {/* Total Pasivo + Capital */}
              <Card className="mb-4">
                <Card.Body className="bg-light">
                  <Row>
                    <Col>
                      <strong>Total Pasivo + Capital</strong>
                    </Col>
                    <Col className="text-end">
                      <strong>{formatCurrency(reportData.ecuacion?.pasivo_mas_capital || 0)}</strong>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Comprobación Ecuación Contable */}
          <Card className="mb-4">
            <Card.Header as="h6" className={reportData.ecuacion?.diferencia === 0 ? 'bg-success text-white' : 'bg-danger text-white'}>
              Comprobación Ecuación Contable
            </Card.Header>
            <Card.Body>
              <Row className="text-center">
                <Col md={4}>
                  <div className="mb-2">
                    <small className="text-muted d-block">Total Activos</small>
                    <strong className="fs-5">{formatCurrency(reportData.ecuacion?.activos || 0)}</strong>
                  </div>
                </Col>
                <Col md={4} className="d-flex align-items-center justify-content-center">
                  <strong className="fs-4 text-muted">=</strong>
                </Col>
                <Col md={4}>
                  <div className="mb-2">
                    <small className="text-muted d-block">Pasivo + Capital</small>
                    <strong className="fs-5">{formatCurrency(reportData.ecuacion?.pasivo_mas_capital || 0)}</strong>
                  </div>
                </Col>
              </Row>
              {reportData.ecuacion?.diferencia !== 0 && (
                <Alert variant="danger" className="mt-3 mb-0 text-center">
                  <strong>Diferencia: {formatCurrency(reportData.ecuacion.diferencia)}</strong>
                </Alert>
              )}
              {reportData.ecuacion?.diferencia === 0 && (
                <Alert variant="success" className="mt-3 mb-0 text-center">
                  <strong>✓ La ecuación contable está balanceada</strong>
                </Alert>
              )}
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  )
}

export default BalanceGeneralReport
